<template>
    <div class="ssh-page">
        <!-- 登录使用功能的提示 -->
        <div v-if="this.$store.state.user.token == undefined || this.$store.state.user.token == ''"
            class="ssh-notice no-select">
            <el-icon class="ssh-notice__icon">
                <Monitor />
            </el-icon>
            <span>登录后可执行共享的 SSH 命令</span>
        </div>

        <!-- 团队筛选 + 搜索过滤 -->
        <ssh-toolbar v-model:selectedTeamId="selectedTeamId" v-model:searchKeyword="searchKeyword" :is-login="isLogin"
            :visible-team-list="visibleTeamList" :team-match-counts="teamMatchCounts" />

        <!-- 服务器及操作列表：按分组分段展示，分组分栏滚动时悬浮，开始 -->
        <div class="ssh-group" v-for="group in sshGroupList" :key="group.key">
            <div class="ssh-group__head" v-if="showGroupHeader">
                <span class="ssh-group__name">{{ group.name }}</span>
                <span class="ssh-group__count">{{ group.list.length }} 台</span>
            </div>
            <div class="ssh-grid no-select">
                <ssh-server-card v-for="item in group.list" :key="item" :machine="item" :is-login="isLogin"
                    @run="handleRunClick" @hold-start="startHold" @hold-cancel="cancelHold" @console="openConsole" />
            </div>
        </div>
        <!-- 空状态 -->
        <div class="ssh-empty" v-if="listLoaded && filteredShareList.length === 0">
            <el-icon :size="26">
                <Search />
            </el-icon>
            <p>{{ searchKeyword || selectedTeamId ? '没有匹配的服务器或命令' : '暂无共享的 SSH 命令' }}</p>
        </div>
        <!-- 服务器及操作列表：结束 -->

        <!-- 执行结果抽屉 -->
        <ssh-log-drawer v-model:visible="drawerPanel.visible" v-model:output="currentRunning.output"
            :machine="currentRunning.machine" :cmd="currentRunning.cmd" :run-finish="currentRunning.runFinish"
            :resolved-cmd="currentRunning.resolvedCmd" @close="handleClose" />

        <!-- 网页控制台抽屉（文件管理作为面板挂在它里面） -->
        <ssh-console-drawer v-model:visible="consoleDrawer.visible" :machine="consoleDrawer.machine"
            :token="this.$store.state.user.token" />
    </div>
</template>

<script>
import { ElMessageBox, ElMessage } from 'element-plus';
import { Search } from '@element-plus/icons';
import request from '../../utils/request.js';
import * as moment from 'moment';
import SshToolbar from './components/SshToolbar.vue';
import SshServerCard from './components/SshServerCard.vue';
import SshLogDrawer from './components/SshLogDrawer.vue';
import SshConsoleDrawer from './components/SshConsoleDrawer.vue';

// 未填写分组时的兜底分组标识
const UNGROUPED_KEY = '__ungrouped__';

export default {
    components: { Search, SshToolbar, SshServerCard, SshLogDrawer, SshConsoleDrawer },
    data() {
        return {
            user: {},
            isLogin: false,
            searchKeyword: '',
            selectedTeamId: '',
            teamList: [],
            teamStorageKey: 'hidoc_ssh_selected_team_id',
            shareList: [],
            listLoaded: false,
            drawerPanel: {
                visible: false
            },
            // SSH 网页控制台抽屉
            consoleDrawer: {
                visible: false,
                machine: {}
            },
            currentRunning: {
                machine: {},
                cmd: {},
                log: {},
                output: '',
                prepareOutput: '',
                refreshTimer: '',
                refreshCount: 0,
                runFinish: false,
                serialNumber: 1,
                resolvedCmd: ''
            },
            progressRefresh: {
                debug: false,
                refreshTimer: null,
                refreshCount: 0
            }
        };
    },
    watch: {
        selectedTeamId(val) {
            // 记住上次选中的团队，刷新后仍保持（清空时也同步记录）
            localStorage.setItem(this.teamStorageKey, val || '');
        }
    },
    computed: {
        keywordFilteredShareList() {
            const list = this.shareList;
            if (!this.searchKeyword) {
                return list;
            }
            const keywords = this.searchKeyword.trim().split(/\s+/).filter(k => k.length > 0);
            if (keywords.length === 0) {
                return list;
            }
            return list.filter(item => {
                return keywords.every(keyword => {
                    const lowerKeyword = keyword.toLowerCase();
                    const matchServerName = item.name.toLowerCase().includes(lowerKeyword);
                    const matchCmdName = item.cmdList.some(cmd => cmd.name.toLowerCase().includes(lowerKeyword));
                    return matchServerName || matchCmdName;
                });
            });
        },
        filteredShareList() {
            if (!this.selectedTeamId) {
                return this.keywordFilteredShareList;
            }
            return this.keywordFilteredShareList.filter(item =>
                (item.teamList || []).some(team => team.id === this.selectedTeamId)
            );
        },
        teamMatchCounts() {
            // 各团队在当前搜索条件下匹配的服务器数量
            const counts = {};
            this.teamList.forEach(team => {
                counts[team.id] = 0;
            });
            this.keywordFilteredShareList.forEach(item => {
                (item.teamList || []).forEach(team => {
                    if (counts[team.id] !== undefined) {
                        counts[team.id]++;
                    }
                });
            });
            return counts;
        },
        visibleTeamList() {
            // 不展示匹配数量为 0 的团队；当前已选中的团队保留，避免下拉框回显成 id
            return this.teamList.filter(team =>
                team.id === this.selectedTeamId || (this.teamMatchCounts[team.id] || 0) > 0
            );
        },
        // 在搜索/团队过滤结果之上，再按分组字段聚合；未填写的归到「未分组」并排在最后
        sshGroupList() {
            const groups = new Map();
            this.filteredShareList.forEach(machine => {
                const name = (machine.groupName || '').trim();
                const key = name || UNGROUPED_KEY;
                if (!groups.has(key)) {
                    groups.set(key, { key, name: name || '未分组', list: [] });
                }
                groups.get(key).list.push(machine);
            });

            const named = [];
            let ungrouped = null;
            groups.forEach(group => {
                if (group.key === UNGROUPED_KEY) {
                    ungrouped = group;
                } else {
                    named.push(group);
                }
            });
            named.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
            return ungrouped ? named.concat(ungrouped) : named;
        },
        // 只有存在已分组的数据时才展示分组标题，全部未分组时保持原来的平铺样式
        showGroupHeader() {
            return this.sshGroupList.some(group => group.key !== UNGROUPED_KEY);
        }
    },
    created() {
        console.log('created 钩子函数被调用');
    },
    mounted() {
        console.log('mounted 钩子函数被调用');
        document.title = 'Hidoc-SSH';

        this.user = this.$store.state.user;
        if (this.user.token == undefined || this.user.token == '') {
            this.isLogin = false;
            this.user.roles = [];
        } else {
            this.isLogin = true;
        }
        if (this.user.token) {
            this.getCurrentUserInfo();
            this.getTeamFilterOptions();
        }
        this.getShareList();

        const urlParams = new URLSearchParams(window.location.search);
        const keyParam = urlParams.get('key');
        if (keyParam) {
            this.searchKeyword = keyParam;
        }
    },
    unmounted() {
        console.log('unmounted 钩子函数被调用');
        clearInterval(this.progressRefresh.refreshTimer);
        clearInterval(this.currentRunning.refreshTimer);
        console.log('定时器已清除');
    },
    methods: {
        progressRefreshFunction(shareCmdList) {
            this.progressRefresh.refreshCount++;
            if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，第 ${this.progressRefresh.refreshCount} 次`);

            let nextDo = false;
            if (shareCmdList && shareCmdList.length > 0) {
                let now = moment();
                if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，共计 ${shareCmdList.length} 个服务器开放指令，当前时间为：${now.format('YYYY-MM-DD HH:mm:ss')}`);
                for (let i = 0; i < shareCmdList.length; i++) {
                    for (let j = 0; j < shareCmdList[i].cmdList.length; j++) {
                        let pgs = 100; // 进度
                        let sec = 0; // 等待秒数
                        let shareItem = shareCmdList[i];
                        let cmdItem = shareCmdList[i].cmdList[j];
                        if (cmdItem.interval && cmdItem.interval > 1 && cmdItem.executeTime) {
                            let executeTime = moment(cmdItem.executeTime);
                            let nextExecuteTime = moment(cmdItem.executeTime).add(cmdItem.interval, 'seconds');
                            sec = nextExecuteTime.diff(now, 'seconds');
                            pgs = Math.round((1 - (sec / cmdItem.interval)) * 100);

                            if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，第 ${i + 1} 个服务器，第 ${j + 1} 个指令，执行时间：${executeTime.format('YYYY-MM-DD HH:mm:ss')}，执行间隔：${cmdItem.interval}， 下次执行时间：${nextExecuteTime.format('YYYY-MM-DD HH:mm:ss')}，应该等待：${sec}秒`);
                            if (sec > 0) {
                                nextDo = true;
                                console.log(`${this.progressRefresh.refreshTimer}，服务器：${shareItem.name}，指令：${cmdItem.name}，【冷却进度：${pgs}%】`);
                            }
                        }
                        cmdItem._waitProgress = pgs > 100 ? 100 : pgs;
                        if (sec > 0) cmdItem._waitSeconds = sec;
                    }
                }
            }
            if (nextDo == false) {
                clearInterval(this.progressRefresh.refreshTimer);
                console.log('所有的进度都已经就绪，定时器已清除');
            }
        },
        getCurrentUserInfo() {
            request({
                url: '/user/currentUserInfo',
                method: 'post'
            }).then(res => {
                if (res.code == 0) {
                    this.user = res.meta.user;
                }
            });
        },
        getTeamFilterOptions() {
            const token = this.$store.state.user.token;
            Promise.all([
                request({ url: '/team/getMyJoinTeams', method: 'post', data: { token } }),
                request({ url: '/team/getOwnerTeams', method: 'post', data: { token } })
            ]).then(results => {
                const teamMap = {};
                results.forEach(res => {
                    if (res.code == 0 && res.data) {
                        res.data.forEach(team => {
                            teamMap[team.id] = { id: team.id, name: team.name };
                        });
                    }
                });
                this.teamList = Object.values(teamMap).sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
                // 恢复上次选中的团队（若该团队已不在列表中则忽略）
                const cachedTeamId = localStorage.getItem(this.teamStorageKey);
                if (cachedTeamId && this.teamList.some(team => team.id === cachedTeamId)) {
                    this.selectedTeamId = cachedTeamId;
                }
            });
        },
        getShareList() {
            request({
                url: '/openapi/serverMan/shareList',
                method: 'post',
                data: {
                    token: this.$store.state.user.token
                }
            }).then(res => {
                clearInterval(this.progressRefresh.refreshTimer);
                if (res.code == 0) {
                    let shareList = res.data || [];
                    // 初始化生产防护锁开关状态：默认关闭
                    shareList.forEach(machine => {
                        (machine.cmdList || []).forEach(cmd => {
                            if (cmd._prodUnlock === undefined) cmd._prodUnlock = false;
                        });
                    });
                    this.progressRefreshFunction(shareList);
                    this.shareList = shareList;
                    this.progressRefresh.refreshTimer = setInterval(() => this.progressRefreshFunction(this.shareList), 1000);
                }
                this.listLoaded = true;
            });
        },
        isProdLocked(machine, cmd) {
            // 生产防护锁：服务器开启后，需长按锁钮解锁才允许执行
            return !!(machine.prodLock && !cmd._prodUnlock);
        },
        startHold(machine, cmd) {
            if (!machine.prodLock || cmd._prodUnlock) return;
            clearInterval(cmd._holdTimer);
            cmd._holdProgress = 0;
            const holdMs = 1000;
            const startTime = Date.now();
            cmd._holdTimer = setInterval(() => {
                const progress = Math.min(100, Math.round(((Date.now() - startTime) / holdMs) * 100));
                cmd._holdProgress = progress;
                if (progress >= 100) {
                    clearInterval(cmd._holdTimer);
                    cmd._holdTimer = null;
                    cmd._holdProgress = 0;
                    cmd._prodUnlock = true;
                    // 解锁后 5 秒内未执行，自动重新上锁（锁钮上有倒计时环）
                    this.startRelockCountdown(cmd);
                }
            }, 20);
        },
        startRelockCountdown(cmd) {
            // 解锁后的自动上锁倒计时：_relockProgress 由 100 递减到 0
            clearInterval(cmd._relockTickTimer);
            const relockMs = 5000;
            const startTime = Date.now();
            cmd._relockProgress = 100;
            cmd._relockTickTimer = setInterval(() => {
                const left = Math.max(0, 1 - (Date.now() - startTime) / relockMs);
                cmd._relockProgress = Math.round(left * 100);
                if (left <= 0) {
                    this.cancelRelockCountdown(cmd);
                }
            }, 50);
        },
        cancelRelockCountdown(cmd) {
            // 结束倒计时并立即上锁
            clearInterval(cmd._relockTickTimer);
            cmd._relockTickTimer = null;
            cmd._relockProgress = 0;
            cmd._prodUnlock = false;
        },
        cancelHold(cmd) {
            clearInterval(cmd._holdTimer);
            cmd._holdTimer = null;
            cmd._holdProgress = 0;
        },
        // 打开 SSH 网页控制台
        openConsole(machine) {
            if (!this.isLogin || !this.$store.state.user.token) {
                ElMessage({ message: '请先登录后再使用控制台', type: 'warning', duration: 3 * 1000 });
                return;
            }
            this.consoleDrawer.machine = machine;
            this.consoleDrawer.visible = true;
        },
        handleRunClick(machine, cmd, event) {
            // 未解锁时点击执行按钮：给一次提示
            if (this.isProdLocked(machine, cmd)) {
                ElMessage({ message: '生产防护锁已启用，请长按锁钮 1 秒解锁', type: 'warning', duration: 3 * 1000 });
                return;
            }
            // 上一次请求还没回来（列表刷新前的空档）：避免连点发出第二次请求
            if (cmd._running) {
                ElMessage({ message: '指令正在执行，请稍候', type: 'warning', duration: 3 * 1000 });
                return;
            }
            // 执行间隔未到（冷却中）：直接在前端拦掉，不发请求
            if (cmd._waitProgress < 100) {
                ElMessage({ message: `冷却中，${cmd._waitSeconds || 0} 秒后可执行`, type: 'warning', duration: 3 * 1000 });
                return;
            }
            // 执行后立即上锁
            if (machine.prodLock) {
                this.cancelRelockCountdown(cmd);
            }
            this.runCmd(machine, cmd, event);
        },
        runCmd(machine, cmd, event) {
            ElMessage({ message: `触发执行指令`, type: 'success', duration: 3 * 1000 });
            // ElMessage({ message: `正在执行：${machine.name} ${cmd.name} 指令`, type: 'success', duration: 3 * 1000 });

            console.log('runCmd: ' + cmd.id);
            if (event.shiftKey) {
                console.log(`You clicked with the Shift key pressed`);
            } else {
                console.log(`You clicked without the Shift key pressed`);
            }
            // 重置当前正在运行的命令
            this.currentRunning.serialNumber = 1;
            this.currentRunning.prepareOutput = '';
            this.currentRunning.resolvedCmd = '';

            // 请求期间锁住该命令的执行按钮，避免连点重复提交；列表刷新会带回新的命令对象
            cmd._running = true;

            return request({
                url: '/serverManCmd/run',
                method: 'post',
                data: { token: this.$store.state.user.token, id: cmd.id }
            }).then(res => {
                if (res.code == 0) {
                    console.log(res);
                    this.currentRunning.log = res.meta.log;
                    this.currentRunning.resolvedCmd = res.meta.log.resolvedContentTa || cmd.contentTa;
                    if (!event.shiftKey) {
                        this.openDrawerPanel(machine, cmd);
                    }
                    ElMessage({
                        message: res.msg,
                        type: 'success',
                        duration: 3 * 1000
                    });
                }
                this.getShareList();
            }).finally(() => {
                // 请求失败时不会刷新列表，这里兜底解锁，避免该命令被永久挡住
                cmd._running = false;
            });
        },
        refreshCmdLogOutput() {
            let that = this;
            this.currentRunning.refreshCount--;
            if (this.currentRunning.log && this.currentRunning.refreshCount > 0) {
                return request({
                    url: '/serverManExeLog/serverManOutput',
                    method: 'post',
                    data: { token: this.$store.state.user.token, dialogId: this.currentRunning.log.id, serialNumber: this.currentRunning.serialNumber }
                }).then(res => {
                    if (res.code == 0 && res.count > 0) {
                        // 循环组装输出的内容
                        for (let i = 0; i < res.data.length; i++) {
                            // 设置序号为最新值，来防止重复获取数据
                            this.currentRunning.serialNumber = res.data[i].serialNumber + 1;
                            this.currentRunning.prepareOutput = this.currentRunning.prepareOutput + res.data[i].output;

                            // 前端判断是否已经执行成功了，有执行成功的标记则不再刷新了
                            if (res.data[i].output.indexOf('##hidoc->serverman.run::end') > 0) {
                                this.currentRunning.runFinish = true;
                                clearInterval(this.currentRunning.refreshTimer);
                            }
                        }
                        this.currentRunning.output = '```bash' + '\r\n' + this.currentRunning.prepareOutput + '\r\n' + '```';

                        // 应用滚动
                        this.$nextTick(() => {
                            let outputElement = document.getElementById('outputContainer').parentElement;
                            outputElement.scrollTop = outputElement.scrollHeight - outputElement.clientHeight;
                        });
                    }
                });
            }
        },
        openDrawerPanel(machine, cmd) {
            this.currentRunning.machine = machine;
            this.currentRunning.cmd = cmd;
            this.currentRunning.output = '```bash' + '\r\n' + '... ... 正在获取结果信息 ... ...' + '\r\n' + '```';
            this.drawerPanel.visible = true;
            this.currentRunning.refreshCount = 100;
            this.currentRunning.refreshTimer = setInterval(this.refreshCmdLogOutput, 2 * 1000);
        },
        handleClose() {
            ElMessageBox.confirm('确定要关闭抽屉吗？')
                .then(() => {
                    this.drawerPanel.visible = false;
                    clearInterval(this.currentRunning.refreshTimer);

                    // this.currentRunning.machine = {};
                    // this.currentRunning.cmd = {};
                })
                .catch(() => {
                    // catch error
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.ssh-page {
    /* 局部覆盖主色，与登录页、首页保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --ssh-text: #1f2d3d;
    --ssh-text-sub: #5b6a80;
    --ssh-text-weak: #8a94a6;
    --ssh-line: #e6eaf0;

    /* 浅色画布由外层的 .app-main-body 提供，这里只负责内容排布 */
    color: var(--ssh-text);
}

/* ---------- 未登录提示 ---------- */
.ssh-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 10px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-size: 13px;
}

.ssh-notice__icon {
    flex: 0 0 auto;
    font-size: 15px;
}

/* ---------- 服务器卡片瀑布流 ---------- */
/* 多栏布局：卡片按列自上而下紧密排列，行与行之间不会因为"对齐最高卡片"而留出空白 */
.ssh-grid {
    column-width: 300px;
    column-gap: 16px;
    /* 与上方分组分栏的间距；间距不放分栏上，保证悬浮交接时位置准确 */
    margin-top: 14px;
    /* 分组之间的留白留在分组内部，下一个分组的分栏才能无缝顶走上一个 */
    padding-bottom: 8px;
}

/* ---------- 分组分栏（滚动时悬浮） ---------- */
/* 每个分组是独立的块，分栏的悬浮范围被限制在本分组内，
   下一个分组的分栏滚到顶部时，当前分栏会被顶走 */
.ssh-group__head {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary-light-7);
    border-left: 3px solid var(--el-color-primary);
    border-radius: 8px;
    background: linear-gradient(90deg, var(--el-color-primary-light-8) 0%, var(--el-color-primary-light-9) 100%);
    box-shadow: 0 8px 16px -14px rgba(47, 111, 237, 0.9);
}

.ssh-group__name {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: var(--el-color-primary-dark-2);
}

.ssh-group__count {
    padding: 2px 8px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 999px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
}

/* ---------- 空状态 ---------- */
.ssh-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 56px 20px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #909399;
    font-size: 13px;
}

.ssh-empty p {
    margin: 0;
}

/* 滚动条：限定在页面作用域内，避免影响全站 */
.ssh-page ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #d8d8d8;
}

.ssh-page ::-webkit-scrollbar-track {
    border-radius: 10px;
}

.ssh-page ::-webkit-scrollbar-thumb {
    background-color: #bfc1c4;
}
</style>
